<script lang="ts">
  import Swal from 'sweetalert2';
  import {AssetService} from '../../../../services';
  import {ASSET_CATEGORY} from '../../../../services/asset';
  import {GoogleStorage} from '../../../../services/google/google.service';
  import {user} from '../../../../store';
  import {ImageGalleryEvent, userImageGalleryStore} from './GalleryResults.svelte';
  import {toasts} from 'svelte-toasts';
  import {writable} from 'svelte/store';
  import {alertToScreenReader} from 'svelte-dnd-action';
  import {doNotHidePopupOnNextClick, hidePopupOnNextClick} from '../../PopupBox/PopupBox.svelte';
  let exceedsMaxWidth = false;
  const assetService = new AssetService();
  let file: HTMLInputElement;
  let files: FileList | null;
  let signedUrl: string | null;
  const uploadProgress = writable(0);
  let hide = 'hidden';

  let uploading = false;
  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  const activateFileInput = () => {
    signedUrl = null;
    file.click();
  };
  const waitForSignedUrl = async () => {
    if (!signedUrl) return;
    else {
      await wait(500);
      return waitForSignedUrl();
    }
  };
  async function uploadImage() {
    if (uploading) return;
    activateFileInput();
    signedUrl = null;
  }

  function determineIfFilesAreEligable(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file && file.type !== 'image/png' && file.type !== 'image/jpeg') {
        throw new Error('Only PNG and JPEG files are allowed');
      }
      console.log('File size is:', file?.size);
      if (file && file?.size > 10000000) {
        alert('File size is too large. Maximum file size is 10MB');
        throw new Error('File size is too large. Maximum file size is 10MB');
      }
    }
    return true;
  }

  function closeWarning() {
    exceedsMaxWidth = false;
  }

  async function getImageWidth(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = event => {
        const img = new Image();
        img.onload = () => {
          console.log('Image loaded successfully');
          resolve(img.naturalWidth);
        };
        img.onerror = error => {
          console.error('Error loading image', error);
          reject(new Error('Error loading image'));
        };
        if (event?.target?.result) img.src = event?.target?.result as string;
      };

      reader.onerror = error => {
        console.error('Error reading file', error);
        reject(new Error('Error reading file'));
      };

      reader.readAsDataURL(file);
    });
  }

  async function fileUploadNow() {
    uploading = true;
    uploadProgress.set(0);
    const userId = $user?.details?.owner;
    if (!userId) throw new Error('User id not set.');

    if (!files || !files.length) return console.warn('No files selected');
    try {
      determineIfFilesAreEligable(files); // Throws an errror that stops furrther execution if check does not pass
    } catch (err: any) {
      doNotHidePopupOnNextClick(true);
      Swal.fire({
        title: 'Whoops Something went wrong',
        text: err.message,
        icon: 'error',
        didClose: () => {
          uploading = false;
          hidePopupOnNextClick();
        }
      });
      uploading = false;
      return;
    }
    const fileLength = files.length;
    let tempProg = 0;
    let completed = 0;
    let uploaded = 0;
    for (let i = 0; i < files?.length; i++) {
      const file = files?.item(i);
      console.log('file', file);
      if (!file) continue;
      const filename = file?.name;
      if (file && file.type !== 'image/png' && file.type !== 'image/jpeg') {
        alert('Only PNG and JPEG files are allowed');
        continue;
      }

      try {
        const imageWidth = await getImageWidth(file);
        if (imageWidth > 2048) {
          exceedsMaxWidth = true;
          // alert(`Image width exceeds the maximum limit of 2048px. The image is ${imageWidth}px wide.`);
          continue;
        }
        const generatedFilename = 'images/' + filename;
        const url = await assetService.generateNames([generatedFilename]);
        const data = url.data;

        if (file) {
          await GoogleStorage.uploadFileFromSignedUrl(data[0], file, {
            onProgress: progress => {
              // tempProg = tempProg + (progress / fileLength);
              tempProg = progress / fileLength + (completed * 100) / fileLength;
              console.log('Upload Progress', tempProg);
              uploadProgress.set(tempProg);
            }
          }).then(resp => {
            console.log('File uploaded', data[0], resp);
            completed++;
          });
          await assetService.saveUploadedAsset([generatedFilename], ASSET_CATEGORY.IMAGE).then(resp => {
            console.log('File uploaded');
            userImageGalleryStore.update(store => ({
              ...store,
              items: [resp.data, ...store.items]
            }));
          });
          uploaded++;
        }
      } catch (err) {
        console.error(err);
        alert('Error reading image');
        continue;
      }
    }
    if (uploaded)
      Swal.fire({
        title: 'Success',
        text: `${uploaded} images uploaded successfully`,
        icon: 'success',
        toast: true,
        position: 'bottom-end',
        showConfirmButton: false,
        timer: 3000
      });
    uploading = false;
    file.value = '';
  }

  $: hide = exceedsMaxWidth ? '' : 'hidden';
</script>

<div
  on:keypress={uploadImage}
  on:click={uploadImage}
  class="w-full text-white {!uploading
    ? 'cursor-pointer bg-primary'
    : 'bg-primary-600'}   relative overflow-hidden rounded-md py-2 text-center hover:bg-primary-600"
>
  {#if uploading}
    Uploading {Math.ceil($uploadProgress)}%
    <div class="absolute left-0 top-0 h-full bg-white" style="width: ${uploadProgress}%"></div>
  {:else}
    Upload Image
  {/if}
</div>
<input type="file" bind:this={file} id="files" bind:files multiple on:change={fileUploadNow} />

<div class="my-2 flex flex-row items-center gap-2 rounded-md border-2 border-gray-200 px-2 py-4 {hide}">
  <i class="fa fa-exclamation-triangle text-md text-orange-500"></i>
  <div class="text-xs text-red-600">
    The file is too large. Please upload images with a maximum width of <strong>2048px</strong>.
  </div>
  <span
    class="cursor-pointer"
    on:click={() => {
      exceedsMaxWidth = false;
    }}
    on:keypress={() => {
      exceedsMaxWidth = false;
    }}
  >
    <i class="fa fa-times cursor-pointer text-gray-600"></i>
  </span>
</div>

<style>
  input[type='file'] {
    display: none;
  }
</style>
